import { ChangeDetectionStrategy, Component, Inject } from '@angular/core'
import * as BaseEventTicketsActions from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.actions'
import { BaseEventTicketsFacade } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.facade'
import { FormGroup } from '@angular/forms'
import { FormUtils } from 'libs/shared/utils/helpers/src/lib/form-utils'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { GbEventTicketFormQuestionsService } from 'libs/shared/event-tickets/utils/src/lib/gb-event-ticket-form-questions.service'
import {
  BaseEventTicketEntity,
  EditEventTicketPayload,
} from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { GbBaseDialogService } from 'libs/shared/utils/dialogs/src/lib/gb-base-dialog-service/gb-base-dialog.service'
import { GbCookieService } from 'libs/shared/utils/services/src/lib/gb-cookie/gb-cookie.service'

@Component({
  selector: 'gb-event-ticket-edit-guest-dialog',
  templateUrl: './gb-event-ticket-edit-guest-dialog.component.html',
  styleUrls: ['./gb-event-ticket-edit-guest-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbEventTicketEditGuestDialogComponent {
  selectedEventTicket$ = this.baseEventTicketsFacade.selectedEventTicket$
  selectedEventTicketCustomFields$ = this.baseEventTicketsFacade.selectedEventTicketCustomFields$
  selectedEventTicketCustomValues$ = this.baseEventTicketsFacade.selectedEventTicketCustomValues$

  guestForm = new FormGroup({})
  questionsForm = new FormGroup({})
  isSelfAssigned = false
  allFormsValid = true

  get canUpdateGuestProfile(): boolean {
    return this.guestForm.status !== 'DISABLED'
  }

  get isCheckIn(): boolean {
    return !!this.data.isCheckIn
  }

  get title(): string {
    return this.isCheckIn ? 'SHARED.EVENT_TICKETS.EDIT_GUEST.CHECK_IN_HEADER' : 'SHARED.EVENT_TICKETS.EDIT_GUEST.HEADER'
  }

  get buttonLabel(): string {
    return this.isCheckIn ? 'SHARED.EVENT_TICKETS.EDIT_GUEST.CHECK_IN_BUTTON' : 'SAVE'
  }

  get isAdminOrVolunteer(): boolean {
    return this.gbCookieService.isAdminOrVolunteer()
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { isCheckIn?: boolean },
    private baseEventTicketsFacade: BaseEventTicketsFacade,
    private notificationService: GbNotificationService,
    private gbEventTicketFormQuestionsService: GbEventTicketFormQuestionsService,
    private gbBaseDialogService: GbBaseDialogService,
    private gbCookieService: GbCookieService,
  ) {}

  hasCustomFields(eventTicket: BaseEventTicketEntity): boolean {
    return eventTicket.ticket_package.custom_fields.length > 0
  }

  shouldShowSubmit(eventTicket: BaseEventTicketEntity): boolean {
    return this.canUpdateGuestProfile || this.hasCustomFields(eventTicket)
  }

  validateForms(form: FormGroup, condition: boolean, onInvalidFn: (form: FormGroup) => void): void {
    if (condition && !form.valid) {
      this.allFormsValid = false
      onInvalidFn(form)
    }
  }

  validateAllForms(): void {
    this.allFormsValid = true // reset to true before validation
    this.validateForms(
      this.guestForm,
      // no need to validate if user not able to update guest's profile
      this.canUpdateGuestProfile,
      (form) => FormUtils.markFieldsAsTouched(form),
    )
    this.validateForms(
      this.questionsForm,
      // apply validation if ticket is self-assigned
      this.isSelfAssigned,
      (form) => FormUtils.markFieldsAsTouched(form),
    )
  }

  onSubmit(): void {
    this.validateAllForms()

    if (!this.allFormsValid) {
      return this.notificationService.error('INVALID_FORM_ERROR_MESSAGE')
    }

    this.baseEventTicketsFacade.dispatch(
      BaseEventTicketsActions.editEventTicket({
        payload: this.createPayload(),
        isCheckIn: this.isCheckIn,
        onSuccess: () => this.gbBaseDialogService.closeDialog(GbEventTicketEditGuestDialogComponent, true),
      }),
    )
  }

  createPayload(): EditEventTicketPayload {
    let payload: EditEventTicketPayload = {
      custom_values: this.gbEventTicketFormQuestionsService.getFormattedCustomValues(this.questionsForm.value),
    }

    if (this.canUpdateGuestProfile) {
      payload = {
        ...payload,
        ...this.guestForm.getRawValue(),
        // cast string 'null' to null
        table_number: JSON.parse(this.guestForm.get('table_number')?.value || '') || null,
      }
    }

    return payload
  }
}
